import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { LifeBuoy, CheckCircle2, XCircle, Copy, Mail, Clock, Search, RefreshCw, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fmtDate } from "@/lib/api";

const statusStyle = (s) => {
  if (s === "resolved") return "bg-green-100 text-green-800 hover:bg-green-100";
  if (s === "rejected") return "bg-slate-200 text-slate-700 hover:bg-slate-200";
  return "bg-amber-100 text-amber-800 hover:bg-amber-100";
};

export default function AdminPasswordTickets() {
  const [tickets, setTickets] = useState([]);
  const [status, setStatus] = useState("open");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [reveal, setReveal] = useState(null); // { email, new_password }

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/password-tickets${status !== "all" ? `?status=${status}` : ""}`);
      setTickets(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const resolve = async (t) => {
    setActing(t.id);
    try {
      const r = await api.post(`/admin/password-tickets/${t.id}/resolve`);
      setReveal({ email: r.data.email, new_password: r.data.new_password });
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setActing(null); }
  };

  const reject = async (t) => {
    setActing(t.id);
    try {
      await api.post(`/admin/password-tickets/${t.id}/reject`);
      toast.success("Ticket rejected");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setActing(null); }
  };

  const copyPw = async () => {
    try { await navigator.clipboard.writeText(reveal.new_password); toast.success("Password copied"); } catch { toast.error("Copy failed"); }
  };

  const shown = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return tickets;
    return tickets.filter((t) => `${t.email} ${t.user_name || ""} ${t.note || ""}`.toLowerCase().includes(n));
  }, [tickets, q]);

  const openCount = tickets.filter((t) => t.status === "open").length;

  return (
    <div data-testid="admin-tickets-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="small-label">Control Panel</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2 flex items-center gap-3">
            Password tickets
            {status === "open" && openCount > 0 && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 font-mono text-xs">{openCount} open</Badge>}
          </h1>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">Students raise these from the sign-in page. Resolving a ticket generates a new default password you can share with them.</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="admin-tickets-refresh"><RefreshCw className="h-4 w-4 mr-1.5" />Refresh</Button>
      </div>

      <div className="mt-6 flex flex-wrap gap-3 items-center">
        <div className="relative max-w-sm w-full">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email, name or note" className="pl-9 bg-white" data-testid="admin-tickets-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44 bg-white" data-testid="admin-tickets-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4 bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <Table data-testid="admin-tickets-table">
          <TableHeader><TableRow>
            <TableHead>Student</TableHead>
            <TableHead>Note</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Raised</TableHead>
            <TableHead>Resolved</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-500">Loading tickets…</TableCell></TableRow>}
            {!loading && shown.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-500">No tickets found.</TableCell></TableRow>}
            {!loading && shown.map((t) => (
              <TableRow key={t.id} data-testid={`admin-ticket-row-${t.id}`}>
                <TableCell>
                  <div className="font-medium flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-slate-400" />{t.email}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {t.user_exists
                      ? <>{t.user_name || "—"} {!t.has_local_password && <span className="text-amber-700">· pinslib only</span>}</>
                      : <span className="text-red-600">No account found</span>}
                  </div>
                </TableCell>
                <TableCell className="text-sm text-slate-600 max-w-xs"><div className="line-clamp-2">{t.note || <span className="text-slate-400">—</span>}</div></TableCell>
                <TableCell><Badge className={statusStyle(t.status)}>{(t.status || "open").toUpperCase()}</Badge></TableCell>
                <TableCell className="text-slate-500 text-sm"><div className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{fmtDate(t.created_at)}</div></TableCell>
                <TableCell className="text-slate-500 text-sm">{t.resolved_at ? fmtDate(t.resolved_at) : "—"}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {t.status === "open" ? (
                    <>
                      <Button size="sm" onClick={() => resolve(t)} disabled={acting === t.id}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white mr-2"
                        data-testid={`admin-ticket-resolve-${t.id}`}>
                        <KeyRound className="h-3.5 w-3.5 mr-1.5" />Generate password
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => reject(t)} disabled={acting === t.id}
                        data-testid={`admin-ticket-reject-${t.id}`}>
                        <XCircle className="h-3.5 w-3.5 mr-1.5" />Reject
                      </Button>
                    </>
                  ) : (
                    <span className="text-xs text-slate-400">by {t.resolved_by || "—"}</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!reveal} onOpenChange={(v) => !v && setReveal(null)}>
        <DialogContent data-testid="admin-ticket-reveal-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-600" />New default password set</DialogTitle>
            <DialogDescription>Share this password with <span className="font-medium text-slate-700">{reveal?.email}</span>. They should sign in and change it from their dashboard.</DialogDescription>
          </DialogHeader>
          <div className="rounded-xl bg-slate-900 text-white px-5 py-4 flex items-center justify-between gap-3">
            <div className="font-mono text-xl tracking-wider" data-testid="admin-ticket-new-password">{reveal?.new_password}</div>
            <Button size="sm" onClick={copyPw} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="admin-ticket-copy-password">
              <Copy className="h-3.5 w-3.5 mr-1.5" />Copy
            </Button>
          </div>
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2">
            <LifeBuoy className="h-4 w-4 flex-none mt-0.5" />This password is shown only once. Copy it now — closing this window won't retrieve it again.
          </p>
          <DialogFooter>
            <Button onClick={() => setReveal(null)} variant="outline" data-testid="admin-ticket-reveal-close">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
